// OverviewTab.js
import React, { useState, useEffect } from 'react';
import { Card, Radio } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line 
} from 'recharts';
import { prepareChartData } from '../../../utils/dataUtils';

/**
 * Overview tab with charts for sales data
 * @param {Object} props - Component props
 * @param {Array} props.reportData - Processed report data
 * @param {boolean} props.excludeCleaningServices - Whether cleaning services are excluded
 * @param {boolean} props.showPricesWithoutVAT - Whether to show prices without VAT
 */
const OverviewTab = ({ reportData, excludeCleaningServices, showPricesWithoutVAT }) => {
  const [chartType, setChartType] = useState('bar');
  const [chartData, setChartData] = useState({
    timeSeries: [],
    statusDistribution: [],
    propertyRevenue: []
  });
  
  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  // Process chart data when reportData or filters change
  useEffect(() => {
    if (reportData && reportData.length > 0) {
      const data = prepareChartData(reportData, {
        excludeCleaningServices,
        showPricesWithoutVAT
      });
      setChartData(data);
    }
    else {
      setChartData({
        timeSeries: [],
        statusDistribution: [],
        propertyRevenue: []
      });
    }
  }, [reportData, excludeCleaningServices, showPricesWithoutVAT]);

  // Create the revenue tooltip formatter based on VAT setting
  const getRevenueTooltipFormatter = () => {
    if (showPricesWithoutVAT) {
      return (value, name, props) => {
        if (name === 'Revenue') {
          return [`£${value.toFixed(2)} (excl. VAT)`, name];
        } else if (name === 'VAT') {
          return [`£${value.toFixed(2)}`, name];
        } else if (name === 'Total') {
          return [`£${value.toFixed(2)} (incl. VAT)`, name];
        }
        return [`£${value.toFixed(2)}`, name];
      };
    } else {
      return (value) => `£${value.toFixed(2)}`;
    }
  };

  // Tooltip formatter for property revenue
  const getPropertyTooltipFormatter = () => {
    if (showPricesWithoutVAT) {
      return (value, name, props) => {
        const entry = props.payload;
        return [
          `£${value.toFixed(2)} (excl. VAT)`,
          `VAT: £${entry.vat.toFixed(2)}`,
          `Total: £${entry.totalWithVAT.toFixed(2)}`,
          name
        ];
      };
    } else {
      return (value, name, props) => [`£${value.toFixed(2)}`, name];
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Revenue Trend Chart */}
        <Card title={showPricesWithoutVAT ? "Revenue Trend (excl. VAT)" : "Revenue Trend"} className="shadow-sm">
          <div className="flex justify-end mb-4">
            <Radio.Group value={chartType} onChange={(e) => setChartType(e.target.value)}>
              <Radio.Button value="bar"><BarChartOutlined /></Radio.Button>
              <Radio.Button value="line">Line</Radio.Button>
            </Radio.Group>
          </div>
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              {chartType === 'bar' ? (
                <BarChart data={chartData.timeSeries}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip formatter={getRevenueTooltipFormatter()} />
                  <Legend />
                  <Bar dataKey="revenue" name={showPricesWithoutVAT ? "Revenue (excl. VAT)" : "Revenue"} fill="#8884d8" />
                  {showPricesWithoutVAT && (
                    <Bar dataKey="vat" name="VAT" fill="#82ca9d" />
                  )}
                </BarChart>
              ) : (
                <LineChart data={chartData.timeSeries}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip formatter={getRevenueTooltipFormatter()} />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" name={showPricesWithoutVAT ? "Revenue (excl. VAT)" : "Revenue"} stroke="#8884d8" />
                  {showPricesWithoutVAT && (
                    <Line type="monotone" dataKey="vat" name="VAT" stroke="#82ca9d" />
                  )}
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Status Distribution Chart */}
        <Card title="Order Status Distribution" className="shadow-sm">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData.statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value, name, props) => [`${value} orders`, name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card 
        title={showPricesWithoutVAT ? "Top Properties by Revenue (excl. VAT)" : "Top Properties by Revenue"} 
        className="shadow-sm mb-6"
      >
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData.propertyRevenue}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name.substring(0, 15)}${name.length > 15 ? '...' : ''}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.propertyRevenue.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip formatter={getPropertyTooltipFormatter()} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </>
  );
};

export default OverviewTab;